import React, { useState } from "react";
import { Check, ChevronLeft, ChevronDown, ChevronUp, Edit, Shield, Building, Mail, FileText, Target, Calendar, Lock, AlertCircle, User, Briefcase, Plus, CircleCheck, HelpCircle, Globe, Code2, Smartphone, Server, Cloud, Cpu, Network } from "lucide-react";
import { intakeTokens } from "./intake-ui";

export type IntakeReviewPageProps = {
  sections: any[];
  answers: Record<string, any>;
  onEdit: (sectionIndex: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  clientName?: string;
  clientEmail?: string;
  isReadOnly?: boolean;
  isPublicTemplate?: boolean;
};

export const IntakeReviewPage: React.FC<IntakeReviewPageProps> = ({
  sections,
  answers,
  onEdit,
  onSubmit,
  isSubmitting,
  clientName,
  clientEmail,
  isReadOnly = false,
  isPublicTemplate = false
}) => {
  // Track expanded/collapsed states for section accordions
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    sections.forEach((sec, idx) => {
      // Default expand the first 3 sections, collapse others by default
      initial[sec.id] = idx < 3;
    });
    return initial;
  });

  const [showAllTargets, setShowAllTargets] = useState(false);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getQuestionIcon = (qid: string) => {
    if (qid === "client_name") return <Building size={14} className="text-slate-400 shrink-0" />;
    if (qid === "client_contact") return <User size={14} className="text-slate-400 shrink-0" />;
    if (qid === "client_email") return <Mail size={14} className="text-slate-400 shrink-0" />;
    if (qid === "client_role") return <Briefcase size={14} className="text-slate-400 shrink-0" />;
    if (qid.includes("window") || qid.includes("date") || qid.includes("timeline")) return <Calendar size={14} className="text-slate-400 shrink-0" />;
    return <HelpCircle size={14} className="text-slate-400 shrink-0" />;
  };

  const getSectionIcon = (id: string) => {
    if (id === "details") return <User size={16} className="text-white" />;
    if (id === "why_now") return <HelpCircle size={16} className="text-white" />;
    if (id === "company") return <Building size={16} className="text-white" />;
    if (id === "data_compliance") return <FileText size={16} className="text-white" />;
    if (id === "targets") return <Target size={16} className="text-white" />;
    if (id === "engagement_preference") return <Calendar size={16} className="text-white" />;
    if (id === "planning_context") return <FileText size={16} className="text-white" />;
    if (id === "authorization") return <Lock size={16} className="text-white" />;
    return <Plus size={16} className="text-white" />;
  };

  const getAnswerDisplay = (q: any) => {
    const val = answers[q.qid];
    const isTbc = answers[`${q.qid}__tbc`] === true;

    if (isTbc) {
      return (
        <span className="text-slate-500 italic bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs">
          Not specified — will use standard defaults
        </span>
      );
    }

    if (val === undefined || val === null || val === "") {
      return <span className="text-slate-400 text-sm font-semibold">—</span>;
    }

    if (q.type === "single_choice") {
      const option = q.options?.find((opt: any) => opt.value === val);
      return <span className="text-sm font-bold text-slate-800">{option?.label || val}</span>;
    }

    if (q.type === "multi_choice") {
      if (!Array.isArray(val) || val.length === 0) {
        return <span className="text-slate-400 text-sm font-semibold">—</span>;
      }
      if (val.includes("__tbc")) {
        return (
          <span className="text-slate-500 italic bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs">
            Not specified — will use standard defaults
          </span>
        );
      }
      return (
        <div className="flex flex-wrap gap-1.5">
          {val.map((v: string) => {
            const option = q.options?.find((opt: any) => opt.value === v);
            return (
              <span key={v} className="bg-slate-100 text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold">
                {option?.label || v}
              </span>
            );
          })}
        </div>
      );
    }

    if (q.type === "yes_no") {
      return (
        <span className="text-sm font-bold text-slate-800 capitalize">{val}</span>
      );
    }

    return <span className="text-sm font-bold text-slate-800 leading-normal">{String(val)}</span>;
  };

  const getSectionCompletion = (section: any) => {
    let answered = 0;
    let total = 0;
    
    section.questions?.forEach((q: any) => {
      // Check visibility condition matching the parent
      const dependsOn = q.dependsOn;
      const isVisible = !dependsOn || answers[dependsOn.qid] === dependsOn.value;
      
      if (isVisible) {
        total++;
        const val = answers[q.qid];
        const isTbc = answers[`${q.qid}__tbc`] === true;
        
        if (val !== undefined && val !== null && val !== "" && !isTbc) {
          if (Array.isArray(val) && val.length === 0) {
            // empty array
          } else {
            answered++;
          }
        }
      }
    });

    return { answered, total };
  };

  // Dynamic Scoping Targets Parsing for Section 5 Category Summary Grid
  const businessAssets = answers.business_assets || [];
  const webTargets: any[] = [];
  const apiTargets: any[] = [];
  const cloudTargets: any[] = [];
  const networkTargets: any[] = [];
  const allParsedTargets: any[] = [];

  businessAssets.forEach((asset: any) => {
    const assetName = asset.asset_name || "Unnamed Asset";
    asset.assessment_scopes?.forEach((scope: any) => {
      const type = scope.assessment_type || "";
      scope.targets?.forEach((target: any) => {
        const val = target.target_value || target.target_name || "Unnamed Target";
        const env = target.environment_stage || "PRODUCTION";
        const parsed = {
          value: val,
          assetName,
          type,
          env,
          typeLabel: type.replace(/_/g, " ")
        };
        allParsedTargets.push(parsed);

        if (type.includes("WEB_APP") || type.includes("MOBILE_APP") || type.includes("AI_LLM")) {
          webTargets.push(parsed);
        } else if (type.includes("API")) {
          apiTargets.push(parsed);
        } else if (type.includes("CLOUD") || type.includes("M365")) {
          cloudTargets.push(parsed);
        } else if (type.includes("NETWORK") || type.includes("FIREWALL") || type.includes("VPN")) {
          networkTargets.push(parsed);
        } else {
          webTargets.push(parsed); // fallback
        }
      });
    });
  });

  const displayedTargets = showAllTargets ? allParsedTargets : allParsedTargets.slice(0, 5);

  return (
    <div className="space-y-6">
      
      {/* ── Header Title & Banner ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 select-none text-left">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">
            {isReadOnly ? "Scoping Details" : "Review your scope"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isReadOnly
              ? "View the details of this scoping session."
              : "Review all your answers before submitting. You can edit any section if needed."}
          </p>
        </div>
        {!isReadOnly && !isPublicTemplate && (
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#58CC02]">
            <span className="w-2 h-2 rounded-full bg-[#58CC02] animate-pulse" />
            Autosave on
          </div>
        )}
      </div>

      {/* ── Banner Alert: Ready to submit or Read-only ── */}
      {!isReadOnly ? (
        <div className="flex items-center gap-3 text-sm font-bold text-[#2e7d32] bg-[#e8f5e9] border border-[#2e7d32]/10 rounded-2xl p-4 text-left select-none shadow-sm">
          <div className="w-6 h-6 rounded-full bg-[#2e7d32]/10 flex items-center justify-center shrink-0">
            <Check size={14} strokeWidth={4} />
          </div>
          <div>
            Everything looks good! <span className="font-semibold text-[#2e7d32]/90">You&apos;re ready to submit your scope for review.</span>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-sm font-bold text-amber-850 bg-amber-50 border border-amber-200/50 rounded-2xl p-4 text-left select-none shadow-sm animate-in fade-in duration-200">
          <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 shrink-0">
            <Lock size={14} />
          </div>
          <div>
            This scoping questionnaire is read-only. Access has been delegated to you.
          </div>
        </div>
      )}

      {/* ── Section Accordions Grid ── */}
      <div className="space-y-4">
        {sections.map((section: any, sectionIdx: number) => {
          const { answered, total } = getSectionCompletion(section);
          const isExpanded = expandedSections[section.id] === true;
          
          return (
            <div key={section.id} className="bg-white border border-slate-200/90 rounded-2xl shadow-[0_1px_3px_rgba(8,21,73,0.02)] overflow-hidden transition text-left">
              
              {/* Accordion Header */}
              <div 
                onClick={() => toggleSection(section.id)}
                className="px-6 py-5 flex items-center justify-between cursor-pointer hover:bg-slate-50/40 select-none border-b border-slate-100"
              >
                <div className="flex items-center gap-4">
                  {/* Dark Blue Icon Wrapper */}
                  <div className="w-9 h-9 rounded-xl bg-[#010D2C] flex items-center justify-center shrink-0 shadow-sm">
                    {getSectionIcon(section.id)}
                  </div>
                  <div>
                    <div className="flex items-center flex-wrap gap-2.5">
                      <h3 className="text-sm font-extrabold text-slate-900 leading-snug">
                        {section.title}
                      </h3>
                      {total > 0 && (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 shrink-0">
                          {answered}/{total} completed
                        </span>
                      )}
                    </div>
                    <p className="text-[11.5px] text-slate-400 mt-0.5 font-medium leading-tight">
                      {section.id === "details" ? "Personal & contact information" : 
                       section.id === "why_now" ? "Context & motivation" :
                       section.id === "company" ? "Company & team information" :
                       section.id === "data_compliance" ? "Data handling & compliance" :
                       section.id === "targets" ? "Assets & systems in scope" :
                       section.id === "engagement_preference" ? "Timeline & engagement type" :
                       section.id === "planning_context" ? "Engagement approach & stakeholders" :
                       section.id === "authorization" ? "Access & permissions" :
                       "Additional Information"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3.5" onClick={(e) => e.stopPropagation()}>
                  {/* Inline list values for collapsed rows */}
                  {!isExpanded && section.id === "data_compliance" && answers.data_sensitivity?.length > 0 && (
                    <div className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500 font-semibold mr-2">
                      {answers.data_sensitivity.slice(0, 3).map((ds: string) => (
                        <span key={ds} className="bg-slate-50 px-2 py-0.5 rounded border border-slate-150">{ds}</span>
                      ))}
                      {answers.data_sensitivity.length > 3 && <span>+{answers.data_sensitivity.length - 3}</span>}
                    </div>
                  )}

                  {!isExpanded && section.id === "targets" && allParsedTargets.length > 0 && (
                    <span className="hidden lg:inline text-xs text-slate-500 font-semibold mr-2">
                      {allParsedTargets.length} target{allParsedTargets.length !== 1 ? 's' : ''} added
                    </span>
                  )}

                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => onEdit(sectionIdx)}
                      className="px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-[11px] font-extrabold text-slate-700 hover:bg-slate-50 transition flex items-center gap-1.5 active:scale-95 shadow-sm"
                    >
                      <Edit size={11} className="text-slate-500" />
                      Edit
                    </button>
                  )}

                  <button 
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="text-slate-400 hover:text-slate-700 p-1 transition"
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>

              {/* Accordion Content Panel */}
              {isExpanded && (
                <div className="p-6 bg-white animate-fadeIn">
                  
                  {/* Special Render: Section 5 Targets Scope */}
                  {section.id === "targets" ? (
                    <div className="space-y-6">
                      
                      {/* Category Cards Summary */}
                      <div className="space-y-3">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Targets by category</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          
                          {/* Web Applications */}
                          {webTargets.length > 0 && (
                            <div className="border border-slate-150 rounded-xl p-4 bg-slate-50/20 space-y-2">
                              <div className="flex items-center gap-2 text-[#58CC02]">
                                <span className="w-5 h-5 rounded bg-[#58CC02]/10 flex items-center justify-center shrink-0">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                                </span>
                                <span className="text-[11.5px] font-extrabold uppercase text-slate-800">Web Application</span>
                              </div>
                              <div className="text-[10px] font-bold text-slate-400">{webTargets.length} target{webTargets.length !== 1 ? 's' : ''}</div>
                              <ul className="text-[11px] font-semibold text-slate-500 space-y-1 pt-1.5">
                                {webTargets.slice(0, 3).map((wt, i) => (
                                  <li key={i} className="truncate">• {wt.value}</li>
                                ))}
                                {webTargets.length > 3 && (
                                  <li className="text-blue-600 text-[10px] font-bold">+{webTargets.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* API Service */}
                          {apiTargets.length > 0 && (
                            <div className="border border-slate-150 rounded-xl p-4 bg-slate-50/20 space-y-2">
                              <div className="flex items-center gap-2 text-cyan-600">
                                <span className="w-5 h-5 rounded bg-cyan-50 flex items-center justify-center shrink-0">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                                </span>
                                <span className="text-[11.5px] font-extrabold uppercase text-slate-800">API Service</span>
                              </div>
                              <div className="text-[10px] font-bold text-slate-400">{apiTargets.length} target{apiTargets.length !== 1 ? 's' : ''}</div>
                              <ul className="text-[11px] font-semibold text-slate-500 space-y-1 pt-1.5">
                                {apiTargets.slice(0, 3).map((at, i) => (
                                  <li key={i} className="truncate">• {at.value}</li>
                                ))}
                                {apiTargets.length > 3 && (
                                  <li className="text-blue-600 text-[10px] font-bold">+{apiTargets.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Cloud / Infrastructure */}
                          {cloudTargets.length > 0 && (
                            <div className="border border-slate-150 rounded-xl p-4 bg-slate-50/20 space-y-2">
                              <div className="flex items-center gap-2 text-[#58CC02]">
                                <span className="w-5 h-5 rounded bg-[#58CC02]/10 flex items-center justify-center shrink-0">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                                </span>
                                <span className="text-[11.5px] font-extrabold uppercase text-slate-800">Cloud / Infra</span>
                              </div>
                              <div className="text-[10px] font-bold text-slate-400">{cloudTargets.length} target{cloudTargets.length !== 1 ? 's' : ''}</div>
                              <ul className="text-[11px] font-semibold text-slate-500 space-y-1 pt-1.5">
                                {cloudTargets.slice(0, 3).map((ct, i) => (
                                  <li key={i} className="truncate">• {ct.value}</li>
                                ))}
                                {cloudTargets.length > 3 && (
                                  <li className="text-blue-600 text-[10px] font-bold">+{cloudTargets.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Network */}
                          {networkTargets.length > 0 && (
                            <div className="border border-slate-150 rounded-xl p-4 bg-slate-50/20 space-y-2">
                              <div className="flex items-center gap-2 text-indigo-600">
                                <span className="w-5 h-5 rounded bg-indigo-50 flex items-center justify-center shrink-0">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
                                </span>
                                <span className="text-[11.5px] font-extrabold uppercase text-slate-800">Network</span>
                              </div>
                              <div className="text-[10px] font-bold text-slate-400">{networkTargets.length} target{networkTargets.length !== 1 ? 's' : ''}</div>
                              <ul className="text-[11px] font-semibold text-slate-500 space-y-1 pt-1.5">
                                {networkTargets.slice(0, 3).map((nt, i) => (
                                  <li key={i} className="truncate">• {nt.value}</li>
                                ))}
                                {networkTargets.length > 3 && (
                                  <li className="text-blue-600 text-[10px] font-bold">+{networkTargets.length - 3} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                        </div>
                      </div>

                      {/* Detailed Target List Table */}
                      {allParsedTargets.length > 0 && (
                        <div className="space-y-3 pt-2">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">All {allParsedTargets.length} targets</h4>
                          <div className="border border-slate-150 rounded-xl overflow-hidden divide-y divide-slate-100 bg-white">
                            {displayedTargets.map((target, tIdx) => {
                              const getTargetIcon = (type: string) => {
                                const t = type.toUpperCase();
                                if (t.includes("WEB_APP")) return <Globe size={14} className="text-blue-600 shrink-0" />;
                                if (t.includes("API")) return <Server size={14} className="text-cyan-600 shrink-0" />;
                                if (t.includes("MOBILE_APP")) return <Smartphone size={14} className="text-purple-600 shrink-0" />;
                                if (t.includes("CODE_REVIEW")) return <Code2 size={14} className="text-slate-600 shrink-0" />;
                                if (t.includes("CLOUD") || t.includes("M365")) return <Cloud size={14} className="text-emerald-600 shrink-0" />;
                                if (t.includes("NETWORK") || t.includes("FIREWALL") || t.includes("VPN")) return <Network size={14} className="text-indigo-600 shrink-0" />;
                                if (t.includes("AI_LLM")) return <Cpu size={14} className="text-pink-600 shrink-0" />;
                                return <Target size={14} className="text-blue-600 shrink-0" />;
                              };
                              return (
                                <div key={tIdx} className="px-4 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm hover:bg-slate-50/30 transition">
                                  <div className="flex items-center gap-2.5">
                                    {getTargetIcon(target.type)}
                                    <span className="font-extrabold text-slate-800 break-all">{target.value}</span>
                                  </div>
                                  <div className="flex items-center gap-4 ml-6 sm:ml-0 text-xs font-bold select-none shrink-0">
                                    <span className="text-slate-400 capitalize">{target.typeLabel.toLowerCase()}</span>
                                    <span className="text-slate-400 capitalize">{target.env.toLowerCase()}</span>
                                    <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-100">In Scope</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {allParsedTargets.length > 5 && (
                            <button
                              type="button"
                              onClick={() => setShowAllTargets(!showAllTargets)}
                              className="w-full py-2 bg-slate-50 border border-slate-150 hover:bg-slate-100 text-xs font-bold text-slate-600 rounded-xl transition-all flex items-center justify-center gap-1 mt-1"
                            >
                              <span>{showAllTargets ? "Show fewer targets" : `Show all ${allParsedTargets.length} targets`}</span>
                              <ChevronDown size={14} className={`transition-transform duration-200 ${showAllTargets ? "rotate-180" : ""}`} />
                            </button>
                          )}
                        </div>
                      )}

                    </div>
                  ) : (
                    /* Default Render: Grid of key-values with icons */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                      {section.questions?.map((q: any) => {
                        if (q.dependsOn && answers[q.dependsOn.qid] !== q.dependsOn.value) {
                          return null;
                        }

                        return (
                          <div key={q.qid} className="space-y-1.5">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-400 select-none">
                              {getQuestionIcon(q.qid)}
                              <span className="uppercase tracking-wider">{q.label}</span>
                              {q.required && <span className="text-[#58CC02]">*</span>}
                            </div>
                            <div className="pl-6 text-left">
                              {getAnswerDisplay(q)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* ── Submit Action Panel Footer ── */}
      {!isReadOnly && (
        <div className="bg-[#fcfdfe] border border-slate-200/90 rounded-2xl p-5 shadow-[0_2px_12px_rgba(8,21,73,0.02)] flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 text-left select-none">
          
          {/* Left Side: Back button */}
          <button
            type="button"
            onClick={() => onEdit(0)}
            className="px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-extrabold text-slate-700 hover:bg-slate-50 transition-all flex items-center gap-1.5 active:scale-95 shadow-sm"
          >
            <ChevronLeft size={16} />
            <span>Back to form</span>
          </button>

          {/* Right Side: Submit trigger */}
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-extrabold transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none shrink-0"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                <span>Submitting…</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
                <span>Submit scope for review</span>
              </>
            )}
          </button>

        </div>
      )}

    </div>
  );
};
